import type { ProfileCircleColor } from 'zaptic-types'
import type { Dictionary } from '../../lib/functional/dictionary.no-deps'
import type { Nullable, UUID } from '../../lib/typeHelpers.no-deps'
import type { LoopPath } from '../flows/LoopPath.no-deps'
import type * as Flows from '../flows/types.no-deps'
import type * as Guide from '../guides/types.no-deps'
import type * as Report from '../reports/types.no-deps'
import type { AppUnitType, KnownUnit } from '../units/types.no-deps'
import type { WorkflowVersionIdentifier } from '../workflows'
import type { CachedUnitData } from '../units/CachedUnitData.no-deps'

export interface Answer {
    label: string
    value: Report.AnswerValue
    timestamp: string
    unitTypeIds?: number[]
    dynamicElement?: boolean
}

export interface FormGroup {
    id: number
    answers: Answer[]
}

export interface NodeGroup {
    cycle: number
    formGroups: FormGroup[]
    answers: Answer[]
}

export interface UnitGroup {
    instance: UUID
    name: string
    image: string
    nodeGroups: NodeGroup[]
    formGroups: FormGroup[]
    answers: Answer[]
}

export interface ActivityGroup {
    id: number
    name: string
    color: string
    logo: string
    unitGroups: UnitGroup[]
    nodeGroups: NodeGroup[]
    formGroups: FormGroup[]
    answers: Answer[]
}

export interface FlowGroupUnit {
    id: number
    name: string
    image?: string
}

export function isAnswerFlowItem(item: FlowItem): item is AnswerFlowItem {
    return item.type === 'answer'
}

export function isElementFlowItem(item: FlowItem): item is ElementFlowItem {
    return item.type === 'element'
}

export type AnswerFlowItem = {
    type: 'answer'
    answer: Answer
    loopPath?: LoopPath
}

export type ElementFlowItem<E extends Flows.Element = Flows.TextContentElement> = {
    type: 'element'
    element: E
    loopPath: LoopPath
}

export type FlowItem<E extends Flows.Element = Flows.TextContentElement> = ElementFlowItem<E> | AnswerFlowItem

export interface FlowGroup {
    type: 'FlowGroup'
    loopPath: LoopPath
    units: FlowGroupUnit[]
    items: FlowItem[]
}

export interface ViewableReport {
    answers: Answer[]
    flowGroups?: FlowGroup[]
    flow: Nullable<Flows.Flow>
    language?: string
    cachedUnitData: CachedUnitData
    subjectUnit?: KnownUnit
}

export interface ViewablePageReport extends ViewableReport {
    title: string
    isLegacy: boolean
    activityGroups: ActivityGroup[]
    flowGroups: FlowGroup[]
    unitsInAnswers: KnownUnit[]
    unitTypesInAnswers: AppUnitType[]
    subjectUnit?: KnownUnit | null
    reportDetails: Report.ReportDetailMap
    photoUrls: Report.PhotoUrls
    guideLookup: Dictionary<Guide.SyncGuide>
    workflow: WorkflowVersionIdentifier
    originId?: number // TODO [ZAP-3075] can this be dropped?
    ancestorReports?: UUID[]
}

export interface ViewableReportData extends ViewablePageReport {
    reportId: number
    reportClientId: UUID
}

export type ReportGuideData = {
    /** ReportClientId of the main report will be stored in the 'root' */
    root: Report.ReportClientId
    ancestorIds?: Report.ReportClientId[]
    /**
     * 'reports' is a record of ReportClientId and ViewableReportData.
     * It has root's ReportClientId and ViewableReportData.
     * If there are any ancestor reports for the root, thier data will be stored in 'reports' as well.
     */
    reports: Record<Report.ReportClientId, ViewableReportData>
}

/** data for displaying an ancestor report (ie the history of a leaf report)  */
export interface HistoricalReport {
    report: Report.ReportGetOutput
    reportAuthor: User
    reportPostId: UUID | null
    reportSubjectUnit: KnownUnit | null
    reportViewData: ViewableReportData
    originInfo: OriginInfo | null // May be an action that created this report
}

export interface User {
    id: number
    name: string
    profileCircleColor: ProfileCircleColor
    profileImageUrl: string | null
}

export interface OriginInfo {
    isAction: boolean
    createdBy: User
    createdDate: Date
    createdManually: boolean
    description: string
}
